// Shared helpers for seller store links (/store/<code>).
// Used by App.tsx (opening a store), StorePage.tsx (rendering one) and
// ActiveSellerDashboard.tsx (showing/copying the seller's own link).

export const STORE_PATH_PREFIX = "/store/";
export const PENDING_STORE_KEY = "pending_store_link";
// A store link a logged-out visitor opened is remembered across the Google
// sign-in redirect (which reloads the page at "/"). It expires so an old,
// abandoned link can never hijack a later normal visit.
const PENDING_TTL_MS = 30 * 60 * 1000;

export function parseStoreCodeFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/store\/([A-Za-z0-9_-]{6,64})\/?$/);
  return match ? match[1] : null;
}

export function buildStoreUrl(code: string): string {
  return `${window.location.origin}${STORE_PATH_PREFIX}${code}`;
}

// The live site's address. A QR code gets printed and posted, so it must NEVER
// point at whatever address the dashboard happened to be opened on (localhost,
// a Netlify preview, a lookalike domain). It always uses this fixed address.
export const PUBLIC_SITE_ORIGIN = "https://inuamarketplace.com";

export function buildQrStoreUrl(code: string): string {
  return `${PUBLIC_SITE_ORIGIN}${STORE_PATH_PREFIX}${code}`;
}

export function setPendingStore(code: string) {
  try {
    localStorage.setItem(
      PENDING_STORE_KEY,
      JSON.stringify({ code, ts: Date.now() })
    );
  } catch {
    /* storage unavailable — the link just won't survive the redirect */
  }
}

export function readPendingStore(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_STORE_KEY);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw);
    if (!code || Date.now() - ts > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_STORE_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearPendingStore() {
  try {
    localStorage.removeItem(PENDING_STORE_KEY);
  } catch {
    /* ignore */
  }
}

// Which store (if any) the app should open on boot: the URL wins, otherwise
// a link remembered from before sign-in.
export function getInitialStoreCode(): string | null {
  if (typeof window === "undefined") return null;
  return parseStoreCodeFromPath(window.location.pathname) || readPendingStore();
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Students / recent graduates -> "<institution>, <county>".
// Young professionals -> "<area>, <county>". SellerFlow already saves a young
// professional's `location` as "<area>, <county>", so it is used as-is
// (with the county appended only if it's somehow missing).
export function formatStoreLocation(seller: any): string {
  if (!seller) return "";
  const county = String(seller.county || "").trim();
  const location = String(seller.location || "").trim();
  const university = String(seller.university || "").trim();

  if (location) {
    if (county && !location.toLowerCase().includes(county.toLowerCase())) {
      return `${location}, ${county}`;
    }
    return location;
  }
  if (university && university.toLowerCase() !== "not specified") {
    return county ? `${university}, ${county}` : university;
  }
  return county;
}
